import { useQuery } from '@tanstack/react-query';
import { accrualTypes, VALUE_KIND_LABELS, type PlannedAccrualLine } from '@/api/hr';

/**
 * Read-only таблиця ТЧ «Нарахування» наказу (оклад + надбавки/доплати/премії).
 *
 * Спільна для форм наказів прийому та переміщення. Назва виду нарахування
 * резолвиться через довідник accrual-types (ТЧ зберігає лише accrual_type_id).
 * Значення форматується за value_kind (сума/відсоток/тариф).
 */

interface Props {
  lines: PlannedAccrualLine[];
}

const fmtValue = (kind: string | null, value: string | null): string => {
  if (value == null) return '—';
  const n = Number(value);
  if (kind === 'PERCENT') return `${n}%`;
  if (kind === 'RATE') return `${n} (тариф)`;
  return `${n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₴`;
};

const ACTION_LABEL: Record<string, string> = {
  ADD: 'Додати',
  CHANGE: 'Змінити',
  CLOSE: 'Закрити',
};

export default function PlannedAccrualsTable({ lines }: Props) {
  const typesQ = useQuery({
    queryKey: ['accrual-types'],
    queryFn: () => accrualTypes.list(),
  });
  const nameById = new Map((typesQ.data ?? []).map((t) => [t.id, t.name]));

  if (!lines || lines.length === 0) return null;

  const hasAction = lines.some((l) => l.action != null);

  return (
    <div className="field" style={{ gridColumn: 'span 3' }}>
      <label>Нарахування (оклад + надбавки)</label>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.4rem' }}>Вид нарахування</th>
            <th style={{ padding: '0.4rem', textAlign: 'right' }}>Значення</th>
            <th style={{ padding: '0.4rem' }}>Спосіб</th>
            {hasAction && <th style={{ padding: '0.4rem' }}>Дія</th>}
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.id} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '0.4rem' }}>
                {nameById.get(l.accrual_type_id) ?? '—'}
              </td>
              <td style={{ padding: '0.4rem', textAlign: 'right', fontWeight: 600 }}>
                {fmtValue(l.value_kind, l.value)}
              </td>
              <td style={{ padding: '0.4rem', color: '#64748b' }}>
                {l.value_kind ? (VALUE_KIND_LABELS[l.value_kind] ?? l.value_kind) : '—'}
              </td>
              {hasAction && (
                <td style={{ padding: '0.4rem', color: '#64748b' }}>
                  {l.action ? (ACTION_LABEL[l.action] ?? l.action) : '—'}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
